import type { ColumnDef } from '@tanstack/react-table';

import { Button } from '@pesto/ui/components/Button';
import { SmartTable } from '@pesto/ui/features/SmartTable/SmartTable';
import { Eye } from 'lucide-react';

import type { StatementListItem } from './types/Statements.types';

import { StatementQuickView } from './components/StatementQuickView';
import { useStatementQuickView } from './hooks/useStatementQuickView';
import { useStatementsListConfig } from './hooks/useStatementsListConfig';

interface StatementsListProps {
  rows: StatementListItem[];
  loading?: boolean;
  amountOfRows?: number;
  pageCount?: number;
  enableDateRangeFilter?: boolean;
  onOpenStatement: (statementId: string) => void;
  getLoanHref?: (loanId: string) => string;
}

export const StatementsList = ({
  rows,
  loading,
  amountOfRows,
  pageCount,
  enableDateRangeFilter,
  onOpenStatement,
  getLoanHref,
}: StatementsListProps) => {
  const { selectedStatement, openQuickView, closeQuickView } = useStatementQuickView();
  const { filterFields, getColumns, initialTableState } = useStatementsListConfig();

  const handleViewFullDetails = (id: string) => {
    closeQuickView();
    onOpenStatement(id);
  };

  const actionsColumn: ColumnDef<StatementListItem> = {
    id: 'actions',
    header: 'Actions',
    enablePinning: true,
    cell: ({ row }) => (
      <div className="flex gap-1">
        <Button variant="ghost" size="sm" onClick={() => openQuickView(row.original)} title="Quick View">
          <Eye className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="sm" onClick={() => onOpenStatement(row.original.id)}>
          Open
        </Button>
      </div>
    ),
  };

  return (
    <>
      <SmartTable
        title="Statements"
        amountOfRows={amountOfRows}
        rows={rows}
        columns={[actionsColumn, ...getColumns()]}
        filterFields={filterFields}
        loading={loading}
        pageCount={pageCount}
        enableDateRangeFilter={enableDateRangeFilter}
        initialState={{ ...initialTableState, columnPinning: { left: ['actions'], right: [] } }}
      />
      <StatementQuickView
        statement={selectedStatement}
        onClose={closeQuickView}
        onViewFullDetails={handleViewFullDetails}
        getLoanHref={getLoanHref}
      />
    </>
  );
};
